import { EventEmitter } from '@angular/core';
import {
    IDatasourceColumnSQLMaps,
    getDatasourceColumnName,
} from '../../utils/datasource-sql-map/datasource';
import { PromiseCancellationToken } from '../../utils/promise/cancellation-token';
import { QueryCacheService } from '../query-cache.service';
import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from '../query-context/models/IQueryContext';
import { IDataLoader } from './IDataLoader';
import {
    DataLoaderResultStatus,
    IDataLoaderResult,
} from './data-loader-result';

export class DataLoader implements IDataLoader {
    public emitter = new EventEmitter<IDataLoaderResult>();
    public latestData: any = undefined;
    public latestQueryHash: string | undefined = undefined;

    constructor(
        // public id: string,
        public instanceId: string,
        public run: (
            this: DataLoader,
            queryContext: IQueryContext,
            cancellationToken: PromiseCancellationToken
        ) => Promise<void>
    ) {}

    /**
     * Executes a query and retrieves results from the query cache.
     *
     * @param {string} query - The query string to be executed.
     * @param {QueryCacheService} queryCache - An instance of the QueryCacheService to fetch results from.
     *
     * @returns {Promise<any>} - The result of the query. Throws an error if the query execution fails.
     *
     * @throws Will throw an error if the query result status is 'error'.
     *
     * @example
     * const result = await runQuery("SELECT * FROM users", myQueryCacheService);
     */
    public static async runQuery(query: string, queryCache: QueryCacheService) {
        const queryResult = structuredClone(
            await queryCache.subscribeQuery(query)
        );

        // console.log(queryResult);
        if (queryResult.status == 'error') {
            const e = JSON.stringify({ message: queryResult.error.message });
            console.log(e);
            throw Error(e);
        }
        return queryResult;
    }

    /**
     * Separates the requested dimensions into those that are present and those that are missing.
     *
     * @param requestedDims - An array of requested dimensions.
     * @param dsDims - An object mapping dimension IDs to their corresponding dimensions.
     *
     * @returns An object containing two properties: 'present' and 'missing'.
     * 'present' is an array of the requested dimensions that are present.
     * 'missing' is an array of the requested dimensions that are missing.
     */
    public static separatePresentAndMissingDims(
        requestedDims: IQueryContextDatasourceColumn[],
        dsDims: IDatasourceColumnSQLMaps
    ) {
        const missing: IQueryContextDatasourceColumn[] = [];
        const present = requestedDims.reduce(
            (
                acc: IQueryContextDatasourceColumn[],
                dim: IQueryContextDatasourceColumn
            ) => {
                const dsDim = dsDims[dim.id];
                if (dsDim) {
                    acc.push(dim);
                } else {
                    missing.push(dim);
                }
                return acc;
            },
            []
        );

        return { present, missing };
    }

    /**
     * Summarizes the missing columns in the query context.
     *
     * @param missing - An array of missing columns in the query context.
     *
     * @returns An object containing the status of the data loader result and a status message.
     * If there are missing columns, the status is 'unsupported-column' and the message lists the unsupported columns.
     * If there are no missing columns, the status is 'ok' and the message is an empty string.
     */
    public static getMissingColumnsStatus(
        missing: IQueryContextDatasourceColumn[]
    ): {
        pendingDataLoaderResultStatus: DataLoaderResultStatus;
        pendingStatusMessage: string;
    } {
        const pendingDataLoaderResultStatus: DataLoaderResultStatus = (() => {
            if (missing.length > 0) {
                return 'unsupported-column';
            }
            return 'ok';
        })();

        const pendingStatusMessage: string = (() => {
            if (missing.length > 0) {
                return `Unsupported columns: ${missing.map((dsCol) =>
                    getDatasourceColumnName(dsCol.id)
                )}`;
            }
            return '';
        })();

        return { pendingDataLoaderResultStatus, pendingStatusMessage };
    }
}
